// ============================================================================
// PRODUCT STITCHER - Product Switching Component
// ============================================================================

class ProductStitcher extends HTMLElement {
    constructor() {
        super();
        this.productInfoEl = null;
        this.swatchItems = [];
    }

    connectedCallback() {
        this.findProductInfo();
        this.init();
    }

    findProductInfo() {
        this.productInfoEl = document.querySelector('product-info[id^="MainProduct"]');
        if (!this.productInfoEl) {
            setTimeout(() => {
                this.productInfoEl = document.querySelector('product-info[id^="MainProduct"]');
                if (this.productInfoEl) {
                    this.init();
                }
            }, 100);
        }
    }

    init() {
        if (!this.productInfoEl) {
            console.warn('ProductStitcher: product-info element not found');
            return;
        }

        this.swatchItems = this.querySelectorAll('[js-product-switch]');
        this.swatchItems.forEach(swatchItem => {
            swatchItem.removeEventListener('click', this.handleProductSwitch);
            swatchItem.addEventListener('click', this.handleProductSwitch.bind(this));
        });
    }

    handleProductSwitch(event) {
        event.preventDefault();
        event.stopPropagation();

        const clickedItem = event.currentTarget;
        const innerDiv = clickedItem.querySelector('.hb-swatch-item') || clickedItem;
        const productHandle = innerDiv.getAttribute('data-product-handle') || clickedItem.getAttribute('data-product-handle');
        const productUrl = innerDiv.getAttribute('data-product-url') || clickedItem.getAttribute('data-product-url') || `/products/${productHandle}`;

        if (!productHandle) {
            console.warn('ProductStitcher: No product handle found');
            return;
        }

        if (clickedItem.classList.contains('selected') || innerDiv.classList.contains('active')) {
            return;
        }

        this.updateActiveState(clickedItem, innerDiv);
        this.setLoadingState(true);
        this.swapProduct(productUrl, productHandle);
    }

    updateActiveState(activeItem, innerDiv) {
        const group = activeItem.closest('.Meta_stitch_product_list_mainloops_new');
        if (group) {
            group.querySelectorAll('[js-product-switch]').forEach(item => {
                item.classList.remove('selected');
                const inner = item.querySelector('.hb-swatch-item');
                if (inner) {
                    inner.classList.remove('active');
                }
            });
        }
        activeItem.classList.add('selected');
        if (innerDiv) {
            innerDiv.classList.add('active');
        }
    }

    setLoadingState(isLoading) {
        if (isLoading) {
            this.productInfoEl.classList.add('is-loading');
        } else {
            this.productInfoEl.classList.remove('is-loading');
        }
    }

    swapProduct(productUrl, productHandle) {
        const sectionId = this.productInfoEl.getAttribute('data-section');
        const url = `${productUrl}?section_id=${sectionId}`;

        fetch(url, {
            signal: AbortSignal.timeout(10000)
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                return response.text();
            })
            .then(html => {
                const parser = new DOMParser();
                const doc = parser.parseFromString(html, 'text/html');
                const newProductInfo = doc.querySelector(`product-info[id^="MainProduct"]`);

                if (!newProductInfo) {
                    throw new Error('Product info element not found in response');
                }

                this.updateProductInfo(newProductInfo, productUrl);
                this.updateURL(productUrl);
                this.reinitializeSwatches();

                document.dispatchEvent(new CustomEvent('productStitched', {
                    detail: { productHandle, productUrl }
                }));

                this.setLoadingState(false);
            })
            .catch(error => {
                console.error('ProductStitcher: Error swapping product:', error);
                this.setLoadingState(false);
                window.location.href = productUrl;
            });
    }

    updateProductInfo(newProductInfo, productUrl) {
        const newProductId = newProductInfo.getAttribute('data-product-id');
        this.productInfoEl.setAttribute('data-url', productUrl);
        this.productInfoEl.setAttribute('data-product-id', newProductId);
        this.productInfoEl.classList.add('product-switching');

        setTimeout(() => {
            if (typeof HTMLUpdateUtility !== 'undefined') {
                const postProcessCallback = () => {
                    requestAnimationFrame(() => {
                        this.productInfoEl.classList.remove('product-switching');
                        this.productInfoEl.classList.add('product-switched');
                        setTimeout(() => {
                            this.productInfoEl.classList.remove('product-switched');
                        }, 200);
                    });
                };

                HTMLUpdateUtility.viewTransition(
                    this.productInfoEl,
                    newProductInfo,
                    [],
                    [postProcessCallback]
                );
            } else {
                this.productInfoEl.innerHTML = newProductInfo.innerHTML;
                this.productInfoEl.classList.remove('product-switching');
                this.productInfoEl.classList.add('product-switched');
                setTimeout(() => {
                    this.productInfoEl.classList.remove('product-switched');
                }, 200);
            }

            this.reinitializeProductScripts();
        }, 50);
    }

    updateURL(productUrl) {
        if (window.history && window.history.pushState) {
            window.history.pushState({ productUrl }, '', productUrl);
            window.addEventListener('popstate', this.handlePopState.bind(this), { once: true });
        }
    }

    handlePopState(event) {
        if (event.state && event.state.productUrl) {
            window.location.href = event.state.productUrl;
        }
    }

    reinitializeSwatches() {
        this.swatchItems = this.querySelectorAll('[js-product-switch]');
        this.swatchItems.forEach(swatchItem => {
            swatchItem.removeEventListener('click', this.handleProductSwitch);
            swatchItem.addEventListener('click', this.handleProductSwitch.bind(this));
        });
        this.updateActiveStateFromCurrentProduct();
    }

    updateActiveStateFromCurrentProduct() {
        if (!this.productInfoEl) return;
        
        const currentProductId = this.productInfoEl.getAttribute('data-product-id');
        const currentUrl = this.productInfoEl.getAttribute('data-url');
        if (!currentProductId && !currentUrl) return;

        // Extract handle from URL for better matching
        let currentHandle = null;
        if (currentUrl && currentUrl.includes('/products/')) {
            currentHandle = currentUrl.split('/products/')[1].split('?')[0].split('#')[0];
        }

        this.swatchItems.forEach(item => {
            const itemHandle = item.getAttribute('data-product-handle');
            const innerDiv = item.querySelector('.hb-swatch-item');
            
            // Try multiple matching strategies
            let matches = false;
            
            // Match by handle (normalized)
            if (currentHandle && itemHandle) {
                const normalizedCurrent = currentHandle.toLowerCase().trim();
                const normalizedItem = itemHandle.toLowerCase().trim();
                matches = normalizedItem === normalizedCurrent;
            }
            
            // Fallback: match by URL contains handle
            if (!matches && currentUrl && itemHandle) {
                matches = currentUrl.includes(itemHandle);
            }
            
            // Fallback: match by product ID
            if (!matches && currentProductId && item.dataset.productId) {
                matches = item.dataset.productId === currentProductId;
            }
            
            if (matches) {
                item.classList.add('selected');
                if (innerDiv) {
                    innerDiv.classList.add('active');
                }
            } else {
                item.classList.remove('selected');
                if (innerDiv) {
                    innerDiv.classList.remove('active');
                }
            }
        });
    }

    reinitializeProductScripts() {
        document.dispatchEvent(new CustomEvent('productPagePopups:init'));
        document.dispatchEvent(new CustomEvent('pdpAccordion:init'));
        
        const variantSelects = this.productInfoEl.querySelector('variant-selects');
        if (variantSelects && variantSelects.connectedCallback) {
            variantSelects.connectedCallback();
        }

        if (typeof productFilterSync !== 'undefined' && productFilterSync) {
            setTimeout(() => {
                productFilterSync.refresh();
            }, 200);
        }
    }
}

customElements.define('product-stitcher', ProductStitcher);

// ============================================================================
// PRODUCT FILTER SYNCHRONIZATION MANAGER
// ============================================================================

class ProductFilterSync {
    constructor() {
        this.currentSelections = {
            frameColor: null,
            lensColor: null,
            hasAudio: null
        };
        this.allProducts = [];
        this.eventHandlers = new Map();
        this.init();
    }

    init() {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.setup());
        } else {
            this.setup();
        }
    }

    setup() {
        this.collectAllProducts();
        this.setInitialSelections();
        this.setupEventListeners();
        this.filterOptions();
        // Ensure active states are set after a short delay to allow DOM to be fully ready
        setTimeout(() => {
            this.updateActiveStates();
        }, 50);
    }

    refresh() {
        this.collectAllProducts();
        this.setInitialSelections();
        this.filterOptions();
        this.updateActiveStates();
        this.setupEventListeners();
    }

    collectAllProducts() {
        this.allProducts = [];
        
        const selectors = [
            { selector: '[js-frame-color-swatch]', type: 'frame' },
            { selector: '[js-lens-color-swatch]', type: 'lens' },
            { selector: '[js-audio-option]', type: 'audio' }
        ];

        selectors.forEach(({ selector, type }) => {
            document.querySelectorAll(selector).forEach(item => {
                const hasAudioAttr = item.getAttribute('data-has-audio');
                this.allProducts.push({
                    element: item,
                    handle: item.getAttribute('data-product-handle'),
                    frameColor: item.getAttribute('data-frame-color') || '',
                    lensColor: item.getAttribute('data-lens-color') || '',
                    hasAudio: hasAudioAttr === 'true',
                    type
                });
            });
        });
    }

    setInitialSelections() {
        this.currentSelections = {
            frameColor: null,
            lensColor: null,
            hasAudio: null
        };

        const activeFrameSwatch = document.querySelector('[js-frame-color-swatch].selected, [js-frame-color-swatch] .active');
        const activeLensSwatch = document.querySelector('[js-lens-color-swatch].selected, [js-lens-color-swatch] .active');
        const activeAudioOption = document.querySelector('[js-audio-option].active');

        if (activeFrameSwatch) {
            const frameElement = activeFrameSwatch.closest('[js-frame-color-swatch]') || activeFrameSwatch;
            this.currentSelections.frameColor = frameElement.getAttribute('data-frame-color') || '';
            this.currentSelections.hasAudio = this.parseAudioAttribute(frameElement.getAttribute('data-has-audio'));
        }

        if (activeLensSwatch) {
            const lensElement = activeLensSwatch.closest('[js-lens-color-swatch]') || activeLensSwatch;
            this.currentSelections.lensColor = lensElement.getAttribute('data-lens-color') || '';
            if (this.currentSelections.hasAudio === null) {
                this.currentSelections.hasAudio = this.parseAudioAttribute(lensElement.getAttribute('data-has-audio'));
            }
        }

        if (activeAudioOption) {
            this.currentSelections.hasAudio = this.parseAudioAttribute(activeAudioOption.getAttribute('data-has-audio'));
            if (!this.currentSelections.frameColor) {
                this.currentSelections.frameColor = activeAudioOption.getAttribute('data-frame-color') || '';
            }
        }

        // Extract from current URL if needed
        const currentHandle = this.getCurrentProductHandle();
        if (currentHandle) {
            const currentProduct = this.allProducts.find(p => p.handle === currentHandle);
            if (currentProduct) {
                if (!this.currentSelections.frameColor && currentProduct.frameColor) {
                    this.currentSelections.frameColor = currentProduct.frameColor;
                }
                if (!this.currentSelections.lensColor && currentProduct.lensColor) {
                    this.currentSelections.lensColor = currentProduct.lensColor;
                }
                if (this.currentSelections.hasAudio === null && currentProduct.hasAudio !== undefined) {
                    this.currentSelections.hasAudio = currentProduct.hasAudio;
                }
            }
        }
    }

    parseAudioAttribute(value) {
        if (value === 'true') return true;
        if (value === 'false') return false;
        return null;
    }

    getCurrentProductHandle() {
        const currentUrl = window.location.pathname;
        if (currentUrl.includes('/products/')) {
            return currentUrl.split('/products/')[1].split('?')[0];
        }
        return null;
    }

    setupEventListeners() {
        this.removeEventListeners();
        
        this.setupSwatchListeners('[js-frame-color-swatch]', (item) => {
            this.currentSelections.frameColor = item.getAttribute('data-frame-color') || '';
            this.currentSelections.hasAudio = this.parseAudioAttribute(item.getAttribute('data-has-audio'));
            const lensColor = item.getAttribute('data-lens-color') || '';
            if (lensColor && !this.currentSelections.lensColor) {
                this.currentSelections.lensColor = lensColor;
            }
        });

        this.setupSwatchListeners('[js-lens-color-swatch]', (item) => {
            this.currentSelections.lensColor = item.getAttribute('data-lens-color') || '';
            const frameColor = item.getAttribute('data-frame-color') || '';
            if (frameColor && !this.currentSelections.frameColor) {
                this.currentSelections.frameColor = frameColor;
            }
            if (this.currentSelections.hasAudio === null) {
                this.currentSelections.hasAudio = this.parseAudioAttribute(item.getAttribute('data-has-audio'));
            }
        });

        this.setupAudioOptionListeners();

        if (!this.productStitchedHandler) {
            this.productStitchedHandler = () => {
                setTimeout(() => this.refresh(), 300);
            };
            document.addEventListener('productStitched', this.productStitchedHandler);
        }
    }

    setupSwatchListeners(selector, updateSelections) {
        document.querySelectorAll(selector).forEach(item => {
            const handler = (e) => {
                if (item.hasAttribute('disabled') || item.classList.contains('variation-disabled')) {
                    e.preventDefault();
                    e.stopPropagation();
                    return;
                }

                updateSelections(item);
                this.filterOptions();
                
                setTimeout(() => {
                    this.refresh();
                }, 100);
            };
            item.addEventListener('click', handler, true);
            this.eventHandlers.set(item, handler);
        });
    }

    setupAudioOptionListeners() {
        document.querySelectorAll('[js-audio-option]').forEach(item => {
            const handler = (e) => {
                const handle = item.getAttribute('data-product-handle');
                
                // Check for special product handling
                if (ProductAudioFixer.shouldBypassSync(handle)) {
                    const href = item.getAttribute('href');
                    if (href) {
                        window.location.href = href;
                        return;
                    }
                }
                
                if (item.hasAttribute('disabled') || item.classList.contains('variation-disabled')) {
                    e.preventDefault();
                    e.stopPropagation();
                    return;
                }

                e.preventDefault();
                e.stopPropagation();
                
                const hasAudio = this.parseAudioAttribute(item.getAttribute('data-has-audio'));
                const frameColor = item.getAttribute('data-frame-color') || '';
                const lensColor = item.getAttribute('data-lens-color') || '';
                const productHandle = item.getAttribute('data-product-handle');
                const productHref = item.getAttribute('href');
                
                if (!productHandle || !productHandle.trim()) {
                    console.warn('ProductFilterSync: Audio option missing product handle');
                    return;
                }
                
                if (productHref && !productHref.startsWith('/products/')) {
                    console.warn('ProductFilterSync: Audio option href is not a product URL:', productHref);
                    return;
                }
                
                this.currentSelections.hasAudio = hasAudio;
                if (frameColor) this.currentSelections.frameColor = frameColor;
                if (lensColor) this.currentSelections.lensColor = lensColor;
                
                this.filterOptions();
                
                document.querySelectorAll('[js-audio-option]').forEach(opt => {
                    opt.classList.remove('active');
                });
                item.classList.add('active');
                
                this.switchToProduct(productHandle, productHref);
            };
            item.addEventListener('click', handler);
            this.eventHandlers.set(item, handler);
        });
    }

    switchToProduct(productHandle, productHref) {
        // Try to find matching product switch element
        const switchElement = document.querySelector(`[data-product-handle="${productHandle}"][js-product-switch]`);
        if (switchElement) {
            switchElement.click();
            return;
        }
        
        // Try nested elements
        const allSwitchElements = document.querySelectorAll(`[js-product-switch]`);
        const matchingSwitch = Array.from(allSwitchElements).find(el => {
            const elHandle = el.getAttribute('data-product-handle') || 
                          el.querySelector('.hb-swatch-item')?.getAttribute('data-product-handle');
            return elHandle === productHandle;
        });
        
        if (matchingSwitch) {
            matchingSwitch.click();
            return;
        }
        
        // Try ProductStitcher directly
        if (productHref && productHref.startsWith('/products/')) {
            const handleFromHref = productHref.split('/products/')[1].split('?')[0].split('#')[0];
            if (handleFromHref === productHandle) {
                const productStitcher = document.querySelector('product-stitcher');
                if (productStitcher && productStitcher.swapProduct) {
                    try {
                        productStitcher.swapProduct(productHref, productHandle);
                        return;
                    } catch (error) {
                        console.warn('ProductFilterSync: Error calling ProductStitcher.swapProduct:', error);
                    }
                }
                
                if (productHref.startsWith('/products/') && !productHref.includes('/collections/')) {
                    window.location.href = productHref;
                    return;
                }
            }
        }
        
        // Fallback: find matching product
        this.findAndSwitchToMatchingProduct();
    }

    removeEventListeners() {
        this.eventHandlers.forEach((handler, item) => {
            const useCapture = item.hasAttribute('js-frame-color-swatch') || item.hasAttribute('js-lens-color-swatch');
            item.removeEventListener('click', handler, useCapture);
        });
        this.eventHandlers.clear();
    }

    findAndSwitchToMatchingProduct(clickedElement = null) {
        const matchingProducts = this.allProducts.filter(product => {
            const frameMatch = !this.currentSelections.frameColor || 
                              !product.frameColor ||
                              product.frameColor === this.currentSelections.frameColor;
            
            const lensMatch = !this.currentSelections.lensColor || 
                             !product.lensColor ||
                             product.lensColor === this.currentSelections.lensColor;
            
            const audioMatch = this.currentSelections.hasAudio === null || 
                             product.hasAudio === this.currentSelections.hasAudio;

            return frameMatch && lensMatch && audioMatch;
        });

        if (matchingProducts.length > 0) {
            const exactMatch = matchingProducts.find(p => 
                p.frameColor === this.currentSelections.frameColor &&
                p.lensColor === this.currentSelections.lensColor &&
                p.hasAudio === this.currentSelections.hasAudio
            );
            
            const targetProduct = exactMatch || matchingProducts[0];
            
            const switchElement = document.querySelector(`[data-product-handle="${targetProduct.handle}"][js-product-switch]`);
            if (switchElement) {
                switchElement.click();
                return;
            }
            
            if (clickedElement && clickedElement.hasAttribute('href')) {
                const href = clickedElement.getAttribute('href');
                if (href && href.startsWith('/products/') && clickedElement.getAttribute('data-product-handle') === targetProduct.handle) {
                    window.location.href = href;
                    return;
                }
            }
            
            if (targetProduct.handle) {
                const productUrl = `/products/${targetProduct.handle}`;
                if (!productUrl.includes('/collections/')) {
                    window.location.href = productUrl;
                } else {
                    console.error('ProductFilterSync: Attempted to navigate to collection page:', productUrl);
                    this.filterOptions();
                }
            } else {
                console.warn('ProductFilterSync: No valid product handle found for matching product');
                this.filterOptions();
            }
        } else {
            this.filterOptions();
        }
    }

    filterOptions() {
        const filterTypes = [
            { selector: '[js-frame-color-swatch]', type: 'frame' },
            { selector: '[js-lens-color-swatch]', type: 'lens' },
            { selector: '[js-audio-option]', type: 'audio' }
        ];

        filterTypes.forEach(({ selector, type }) => {
            document.querySelectorAll(selector).forEach(item => {
                const productAttrs = {
                    frameColor: item.getAttribute('data-frame-color') || '',
                    lensColor: item.getAttribute('data-lens-color') || '',
                    hasAudio: item.getAttribute('data-has-audio') === 'true'
                };

                const isAvailable = this.shouldShowOption(productAttrs, type);
                this.updateItemAvailability(item, isAvailable);
            });
        });
    }

    updateItemAvailability(item, isAvailable) {
        item.style.display = '';
        item.style.visibility = 'visible';
        
        if (isAvailable) {
            item.style.opacity = '1';
            item.style.pointerEvents = 'auto';
            item.style.cursor = 'pointer';
            item.classList.remove('filtered-out', 'variation-disabled');
            item.setAttribute('aria-disabled', 'false');
            item.removeAttribute('disabled');
        } else {
            item.style.opacity = '0.4';
            item.style.pointerEvents = 'none';
            item.style.cursor = 'not-allowed';
            item.classList.add('filtered-out', 'variation-disabled');
            item.setAttribute('aria-disabled', 'true');
            item.setAttribute('disabled', 'disabled');
        }
    }

    shouldShowOption(productAttrs, filterType) {
        if (!this.currentSelections.frameColor && 
            !this.currentSelections.lensColor && 
            this.currentSelections.hasAudio === null) {
            return true;
        }

        const checkFrame = filterType === 'frame' ? productAttrs.frameColor : this.currentSelections.frameColor;
        const checkLens = filterType === 'lens' ? productAttrs.lensColor : this.currentSelections.lensColor;
        const checkAudio = filterType === 'audio' ? productAttrs.hasAudio : this.currentSelections.hasAudio;

        const hasMatchingProduct = this.allProducts.some(p => {
            const frameMatch = !checkFrame || !p.frameColor || p.frameColor === checkFrame;
            const lensMatch = !checkLens || !p.lensColor || p.lensColor === checkLens;
            const audioMatch = checkAudio === null || p.hasAudio === checkAudio;
            
            return frameMatch && lensMatch && audioMatch;
        });

        if (filterType === 'frame' && this.currentSelections.frameColor) {
            return productAttrs.frameColor === this.currentSelections.frameColor || hasMatchingProduct;
        }
        
        if (filterType === 'lens' && this.currentSelections.lensColor) {
            return productAttrs.lensColor === this.currentSelections.lensColor || hasMatchingProduct;
        }
        
        if (filterType === 'audio' && this.currentSelections.hasAudio !== null) {
            return productAttrs.hasAudio === this.currentSelections.hasAudio || hasMatchingProduct;
        }

        return hasMatchingProduct;
    }

    updateActiveStates() {
        const currentHandle = this.getCurrentProductHandle();
        if (!currentHandle) {
            // Fallback: try to get handle from product-info element
            const productInfoEl = document.querySelector('product-info[id^="MainProduct"]');
            if (productInfoEl) {
                const dataUrl = productInfoEl.getAttribute('data-url');
                if (dataUrl && dataUrl.includes('/products/')) {
                    const handleFromUrl = dataUrl.split('/products/')[1].split('?')[0].split('#')[0];
                    if (handleFromUrl) {
                        // Set frame and lens swatches only (ProductFilterSync's responsibility)
                        this.setActiveStateForHandle(handleFromUrl);
                        // Audio swatches are handled exclusively by ProductAudioFixer (runs last for final authority)
                        ProductAudioFixer.updateAudioActiveState(handleFromUrl);
                        return;
                    }
                }
            }
            return;
        }

        // Set frame and lens swatches only (ProductFilterSync's responsibility)
        this.setActiveStateForHandle(currentHandle);
        // Audio swatches are handled exclusively by ProductAudioFixer (runs last for final authority)
        ProductAudioFixer.updateAudioActiveState(currentHandle);
    }

    setActiveStateForHandle(handle) {
        if (!handle) return;

        // ONLY allow frame and lens. NEVER audio.
        const swatchTypes = [
            { selector: '[js-frame-color-swatch]' },
            { selector: '[js-lens-color-swatch]' }
        ];

        const normalizedCurrent = handle.toLowerCase().trim();

        swatchTypes.forEach(({ selector }) => {
            document.querySelectorAll(selector).forEach(item => {
                const itemHandle = item.getAttribute('data-product-handle');
                if (!itemHandle) return;
                
                const isActive = itemHandle.toLowerCase().trim() === normalizedCurrent;
                item.classList.toggle('selected', isActive);
                
                const innerDiv = item.querySelector('.hb-swatch-item');
                if (innerDiv) innerDiv.classList.toggle('active', isActive);
            });
        });
    }
}

// ============================================================================
// PRODUCT AUDIO FIXER - Handles hardcoded audio option fixes
// ============================================================================

class ProductAudioFixer {
    static CONFIG = {
        'aura-prismatic-smart-glasses-electrochromic-color-changing-sunglasses': false,
        'aura-audio-calm-white-silver-instant-color-changing-sunglasses': true,
        'aura-black-calm-instant-color-changing-sunglasses': false,
        'aura-calm-black-gold-instant-color-changing-sunglasses': false // EXACT LIVE URL HANDLE
    };

    static shouldBypassSync(handle) {
        return handle === 'aura-prismatic-smart-glasses-electrochromic-color-changing-sunglasses';
    }

    static getCurrentProductHandle() {
        const currentUrl = window.location.pathname;
        if (currentUrl.includes('/products/')) {
            return currentUrl.split('/products/')[1].split('?')[0];
        }
        return null;
    }

    static updateAudioActiveState(currentHandle) {
        if (!currentHandle || currentHandle === 'undefined') {
            currentHandle = this.getCurrentProductHandle() || '';
        }
        
        const normalizedHandle = currentHandle.toLowerCase().trim();
        const audioOptions = document.querySelectorAll('[js-audio-option]');
        
        if (audioOptions.length === 0) return;

        // STEP 1: FORCE RESET - Strip BOTH 'active' and 'selected'
        // This stops the "ghost" highlight where one class is removed but the other stays
        audioOptions.forEach(item => {
            item.classList.remove('active', 'selected');
            const inner = item.querySelector('.hb-swatch-item');
            if (inner) inner.classList.remove('active', 'selected');
        });

        // STEP 2: SIMPLIFIED IDENTIFICATION
        // Using includes() without the extra "instant-color-changing" requirement for reliability
        const isNonAudioHandle = normalizedHandle.includes('aura-calm-black-gold') || 
                                 normalizedHandle.includes('aura-black-calm') || 
                                 normalizedHandle.includes('aura-prismatic');
        
        if (isNonAudioHandle) {
            this.activateWithoutAudio(audioOptions);
            return; 
        }

        // STEP 3: STANDARD MATCHING
        if (this.CONFIG.hasOwnProperty(normalizedHandle)) {
            this.activateAudioOption(audioOptions, this.CONFIG[normalizedHandle]);
        } else {
            audioOptions.forEach(item => {
                const itemHandle = item.getAttribute('data-product-handle');
                if (itemHandle && itemHandle.toLowerCase().trim() === normalizedHandle) {
                    item.classList.add('active', 'selected');
                }
            });
        }
    }

    static activateWithoutAudio(audioOptions) {
        let activated = false;
        
        // Pass 1: Ensure "With Audio" is dead
        audioOptions.forEach(item => {
            const isAudioAttr = item.getAttribute('data-has-audio') === 'true';
            if (isAudioAttr) {
                item.classList.remove('active', 'selected');
            }
        });
        
        // Pass 2: Activate "Without Audio"
        audioOptions.forEach(item => {
            const text = item.textContent.trim().toLowerCase();
            const isAudioAttr = item.getAttribute('data-has-audio') === 'true';
            
            // Match by attribute or text
            if (isAudioAttr === false || text.includes('without')) {
                if (!activated) {
                    item.classList.add('active', 'selected');
                    const inner = item.querySelector('.hb-swatch-item');
                    if (inner) inner.classList.add('active', 'selected');
                    activated = true;
                }
            }
        });
    }

    static activateAudioOption(audioOptions, targetHasAudio) {
        let foundMatch = false;
        audioOptions.forEach(item => {
            const itemHasAudio = item.getAttribute('data-has-audio') === 'true';
            if (itemHasAudio === targetHasAudio && !foundMatch) {
                item.classList.add('active', 'selected');
                foundMatch = true;
            }
        });
    }

    static applyFix() {
        const currentHandle = this.getCurrentProductHandle();
        if (!currentHandle) return;
        this.updateAudioActiveState(currentHandle);
    }
}

// ============================================================================
// INITIALIZATION
// ============================================================================

let productFilterSync;

function initializeProductFilterSync() {
    if (!productFilterSync) {
        productFilterSync = new ProductFilterSync();
    } else {
        productFilterSync.refresh();
    }
}

function initialize() {
    // Run 1: Immediate (before anything else)
    ProductAudioFixer.applyFix();
    initializeProductFilterSync();
    
    // Run 2: After a short delay
    setTimeout(() => {
        console.log("Fixer: Second pass...");
        ProductAudioFixer.applyFix();
    }, 100);
    
    // Run 3: After the theme scripts settle
    setTimeout(() => {
        console.log("Fixer: Final UI Sync...");
        ProductAudioFixer.applyFix(); 
        if (productFilterSync) productFilterSync.updateActiveStates();
    }, 500); // 500ms is the "Sweet Spot" for slow Shopify themes
    
    // Run 4: Nuclear option - one more time after everything
    setTimeout(() => {
        console.log("Fixer: Nuclear pass...");
        ProductAudioFixer.applyFix();
    }, 1000);
}

// Run IMMEDIATELY if DOM is already loaded
if (document.readyState !== 'loading') {
    ProductAudioFixer.applyFix();
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        initialize();
    });
} else {
    initialize();
}

// Re-initialize on product swaps
document.addEventListener('productStitched', () => {
    setTimeout(() => {
        if (productFilterSync) {
            productFilterSync.refresh();
        }
        // This MUST run after refresh to overwrite any mistakes it made
        ProductAudioFixer.applyFix();
    }, 450); // Give it extra time to be the last one standing
});

// MutationObserver to catch dynamically added audio options
let audioFixObserver = null;
function setupAudioFixObserver() {
    if (audioFixObserver) return;
    
    audioFixObserver = new MutationObserver((mutations) => {
        let shouldFix = false;
        mutations.forEach((mutation) => {
            if (mutation.type === 'childList') {
                mutation.addedNodes.forEach((node) => {
                    if (node.nodeType === 1) { // Element node
                        if (node.hasAttribute && node.hasAttribute('js-audio-option')) {
                            shouldFix = true;
                        }
                        if (node.querySelectorAll && node.querySelectorAll('[js-audio-option]').length > 0) {
                            shouldFix = true;
                        }
                    }
                });
            }
            if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
                const target = mutation.target;
                if (target.hasAttribute && target.hasAttribute('js-audio-option')) {
                    if (target.classList.contains('active')) {
                        const currentHandle = ProductAudioFixer.getCurrentProductHandle();
                        if (currentHandle) {
                            const normalizedHandle = currentHandle.toLowerCase().trim();
                            // Simplified identification matching the main logic
                            const isNonAudioHandle = normalizedHandle.includes('aura-calm-black-gold') || 
                                                     normalizedHandle.includes('aura-black-calm') || 
                                                     normalizedHandle.includes('aura-prismatic');
                            
                            if (isNonAudioHandle) {
                                const isAudioAttr = target.getAttribute('data-has-audio') === 'true';
                                if (isAudioAttr) {
                                    console.log("Fixer: MutationObserver caught active 'With Audio' on non-audio page! Handle:", currentHandle);
                                    shouldFix = true;
                                }
                            }
                        }
                    }
                }
            }
        });
        
        if (shouldFix) {
            console.log("Fixer: MutationObserver triggered fix");
            setTimeout(() => {
                ProductAudioFixer.applyFix();
            }, 50);
        }
    });
    
    // Observe the document body for changes
    audioFixObserver.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class']
    });
    
    console.log("Fixer: MutationObserver setup complete");
}

// Setup observer after DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        setTimeout(setupAudioFixObserver, 100);
    });
} else {
    setTimeout(setupAudioFixObserver, 100);
}
